import Footer from '@components/Footer';
import Header from '@components/Header';
import MarketingSlogan from '@components/MarketingSlogan';
import { DEFAULT_PAPER_WIDTH, USE_BLS_THEME } from '@constants';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useTheme from '~/theme/ThemeProvider';

function BikeleasingLayout({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gridTemplateRows: { xs: 'auto 1fr', md: '1fr' },
          px: { xs: 2, md: 0 },
          pt: { xs: 2, md: 0 },
        }}
      >
        <Box
          component="header"
          sx={{
            display: 'flex',
            justifyContent: { xs: 'center', md: 'start' },
            marginLeft: { md: 8 },
            paddingBottom: { xs: 2 },
          }}
        >
          <Header />
        </Box>
        <Box
          component="main"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: { xs: 'flex-start', md: 'center' },
            pt: { sm: 6, md: 0 },
            pr: { md: 4 },
          }}
        >
          {children}
        </Box>
      </Box>
      <Footer />
    </Box>
  );
}

function ProbonioLayout({ children }: { children: React.ReactNode }) {
  const appTheme = useTheme();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: appTheme.colors.BACKGROUNDS.CANVAS }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 2.5 },
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Header />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Box
          sx={{
            order: { xs: 1, md: 0 },
            // Grows to fill the leftover space but is allowed to shrink past its
            // content (minWidth: 0) so the right column can keep the card intact.
            flex: { md: 1 },
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: { md: 'url(/backgrounds/probonio.svg)' },
            backgroundRepeat: 'no-repeat',
            // Stretch to fill the column exactly: the whole swoosh stays visible and
            // there's never a crop or an empty gap, at the cost of slight distortion
            // of the (abstract) arcs as the column aspect ratio changes.
            backgroundSize: { md: '100% 100%' },
          }}
        >
          <MarketingSlogan />
        </Box>

        <Box
          component="main"
          sx={{
            order: { xs: 0, md: 1 },
            // Shares the width 50/50 when there is room, but never shrinks below
            // the card (+ padding) so the card never overflows; the left column gives way instead.
            flex: { md: 1 },
            minWidth: { md: DEFAULT_PAPER_WIDTH + 64 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: { xs: 'flex-start', md: 'center' },
            p: { xs: 2, md: 4 },
            pt: { sm: 10 },
            pb: { sm: 5 },
          }}
        >
          {children}
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}

export default function HeroWrapperPage({ children }: { children: React.ReactNode }) {
  useTranslation();

  return USE_BLS_THEME ? <BikeleasingLayout>{children}</BikeleasingLayout> : <ProbonioLayout>{children}</ProbonioLayout>;
}
